/**
 * aC Men's Hub の「案件」（メンズのプロダクトバックログ）の語彙と API 契約（zod schema + 型）。
 *
 * 全社のプロダクトバックログのスプシ（開発タブ「開発案件一覧_メンズ」）を正本のまま読み、区切り行で
 * 分けた区分ごとに案件を並べる。区分の種類・Hub での並べ順・動かせない理由の語彙と、一覧 API の
 * 入出力を API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）で同じ型から扱う。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business メンズの案件一覧（プロダクトバックログのスプシの区分・並び・ずれ・Backlog の値）の語彙と一覧 API の契約を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
import { isCalendarDate } from './calendar-date.js';
import { ProductBacklogWriteWindowSchema } from './product-backlog-window.js';
/**
 * 案件を見る権限（一覧・詳細・記録・メモを見る）。Hub の既存の全ロールに付ける運用。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business メンズの案件一覧を閲覧するために必要な権限を表す
 * @graph-connects none
 */
export const PRODUCT_BACKLOG_PERMISSION = 'product-backlog';
/**
 * 案件を動かす権限（並べ替え・反映・元に戻す・未振り分けの置き場所決め・メモを書く）。
 *
 * 書き込む API は namespace `productBacklogWrite` にまとめ、その配下全体に見る権限と動かす権限の
 * 両方を要求する（app.ts の共通の関所）。この権限だけを持つ専用のロールを作り、付けるのは1人に絞る。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business メンズの案件の並び（全社スプシの行の位置）とメモを変えるために必要な権限を表す。閲覧とは別に配る
 * @graph-connects none
 */
export const PRODUCT_BACKLOG_WRITE_PERMISSION = 'product-backlog-write';
/**
 * 一覧に出す区分の種類。並びは Hub の一覧での並び順（アサインに近い順）で、スプシの行の順とは違う。
 * - unassigned: 未振り分け（起票と新しいバグが末尾に足される区分。一覧では先頭の「置き場所が決まっていない案件」）
 * - dev_waiting: 開発アサイン希望（要件定義済み）
 * - req_waiting: 要件定義アサイン希望
 * - undecided: 優先度未決定
 * - hold: 保留
 * - req_assigned / dev_assigned / light_task / done: 要件定義アサイン済み・開発アサイン済み・軽タスク・完了（見るだけ）
 *
 * Spot Rental の区分は一覧に出さないので、この語彙に含めない。
 *
 * @graph-connects none
 */
export const PRODUCT_BACKLOG_SECTION_KINDS = [
    'unassigned',
    'dev_waiting',
    'req_waiting',
    'undecided',
    'hold',
    'req_assigned',
    'dev_assigned',
    'light_task',
    'done',
];
/** @graph-connects none */
export const ProductBacklogSectionKindSchema = z.enum(PRODUCT_BACKLOG_SECTION_KINDS);
/**
 * Hub で札を動かす対象の区分（置き場所が決まっていない案件と、並べる4つの区分）。
 * これ以外の区分は見るだけ。
 * @graph-connects none
 */
const ARRANGEABLE_SECTION_KINDS = new Set([
    'unassigned',
    'dev_waiting',
    'req_waiting',
    'undecided',
    'hold',
]);
/**
 * その区分が Hub で札を動かす対象か（見るだけの区分なら false）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 区分が Hub で並べる区分（置き場所が決まっていない案件・開発アサイン希望・要件定義アサイン希望・優先度未決定・保留）かを判定する
 * @graph-connects none
 */
export function isArrangeableSectionKind(kind) {
    return ARRANGEABLE_SECTION_KINDS.has(kind);
}
/**
 * 行を動かせない理由（区分に関係なく行そのものが持つ理由）。
 * - no_key: キー（E列）が無い
 * - duplicate_key: 同じキーの行がスプシに2行以上ある
 * - protected_top_rows: 9行目より上（他のタブが9行目からの範囲で読んでいるため）
 * @graph-connects none
 */
export const ProductBacklogLockReasonSchema = z.enum([
    'no_key',
    'duplicate_key',
    'protected_top_rows',
]);
/**
 * 暦の日付（YYYY-MM-DD）。
 * @graph-connects none
 */
const CalendarDateSchema = z.string().refine(isCalendarDate);
/**
 * Backlog の値（BigQuery `pb_sheet_sync`。スプシの写しと同じ出どころ）。
 * @graph-connects none
 */
export const ProductBacklogValuesSchema = z.object({
    issueType: z.string().nullable(),
    status: z.string().nullable(),
    pl: z.string().nullable(),
    mainWorker: z.string().nullable(),
    designNeeded: z.string().nullable(),
    designStatus: z.string().nullable(),
    appNeeded: z.string().nullable(),
    roughPtMin: z.number().nullable(),
    roughPtMax: z.number().nullable(),
    requirementAssignee: z.string().nullable(),
    stestMakeFinDate: CalendarDateSchema.nullable(),
    businessFlowStatus: z.string().nullable(),
    featureListStatus: z.string().nullable(),
    validationStatus: z.string().nullable(),
    releaseDeadline: CalendarDateSchema.nullable(),
});
/**
 * 課題そのものの値（BigQuery `issues_normalized_table`）。起票日は日本時間の日付、詳細は先頭だけ。
 * @graph-connects none
 */
export const ProductBacklogIssueSchema = z.object({
    createdDate: CalendarDateSchema.nullable(),
    descriptionHead: z.string().nullable(),
});
/**
 * 案件1件（スプシの1行）。
 * - key: E列のキー（前後の空白を除く）。無い行は null
 * - title: F列の題名（HYPERLINK の表示文字）。空なら null（画面はキーを出す）
 * - rank: 区分の中で何番目か（1から。一覧に出す行だけで数える）
 * - sheetRequirementAssignee: スプシの「要件定義アサイン」列（人が手で入れた値を書き写しが消さない列）
 * - backlog / issue: 読めた Backlog の値。キーが無い・Backlog に見つからない・読めなかったときは null
 *   （読めなかったかどうかは一覧の `backlogStatus` / `issueStatus` で分かる）
 * @graph-connects none
 */
export const ProductBacklogCardSchema = z.object({
    key: z.string().nullable(),
    title: z.string().nullable(),
    business: z.string().nullable(),
    rowNumber: z.number().int().positive(),
    rank: z.number().int().positive(),
    lockReason: ProductBacklogLockReasonSchema.nullable(),
    sheetRequirementAssignee: z.string().nullable(),
    backlog: ProductBacklogValuesSchema.nullable(),
    issue: ProductBacklogIssueSchema.nullable(),
});
/**
 * 区分1つ。label は区切り行の B列の文言そのまま、cards はスプシの行の順。
 * @graph-connects none
 */
export const ProductBacklogSectionSchema = z.object({
    kind: ProductBacklogSectionKindSchema,
    label: z.string(),
    separatorRowNumber: z.number().int().positive(),
    arrangeable: z.boolean(),
    cards: z.array(ProductBacklogCardSchema),
});
/**
 * スプシの形が想定と違う点。1つでもあれば Hub からの並べ替えを止めて読むだけにする。
 * - missing_header: 2行目に必須の見出し（キー・チケット名・事業・Div）が無い
 * - unknown_separator: 区分の対応表に当てはまらない区切り行がある
 * - ambiguous_separator: 区切り行の文言に ↑ と ↓ の両方がある
 * - duplicate_section: 同じ種類の区分が2つある
 * - missing_section: 並べる区分か未振り分けの区切りが見つからない
 * - unclaimed_rows: どの区切りにも属さない行がある（↑ の区切りの下と ↓ の区切りの上に挟まれた行）
 * - contested_rows: 2つの区切りが同じ行を自分の区分だと指している（↓ の区切りと ↑ の区切りの間の行）
 * @graph-connects none
 */
export const ProductBacklogLayoutIssueSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('missing_header'), header: z.string() }),
    z.object({
        type: z.literal('unknown_separator'),
        rowNumber: z.number().int().positive(),
        label: z.string(),
    }),
    z.object({
        type: z.literal('ambiguous_separator'),
        rowNumber: z.number().int().positive(),
        label: z.string(),
    }),
    z.object({
        type: z.literal('duplicate_section'),
        kind: ProductBacklogSectionKindSchema,
        rowNumbers: z.array(z.number().int().positive()),
    }),
    z.object({ type: z.literal('missing_section'), kind: ProductBacklogSectionKindSchema }),
    z.object({
        type: z.literal('unclaimed_rows'),
        rowNumbers: z.array(z.number().int().positive()),
    }),
    z.object({
        type: z.literal('contested_rows'),
        rowNumbers: z.array(z.number().int().positive()),
    }),
]);
/**
 * 書き写しのずれ: E列のキーと、F列の HYPERLINK が指す `/view/{キー}` のキーが違う行。
 * sectionKind はその行が入っている区分（区分を決められない行は null）。
 * @graph-connects none
 */
export const ProductBacklogDriftSchema = z.object({
    rowNumber: z.number().int().positive(),
    sheetKey: z.string(),
    linkKey: z.string(),
    title: z.string().nullable(),
    sectionKind: ProductBacklogSectionKindSchema.nullable(),
});
/**
 * 書き込みのスイッチの今の状態（緊急停止用。既定はオン）。
 * - on: 設定が無い、またはオン
 * - off: 設定でオフにしてある
 * - unknown: 設定を読めなかった・形が壊れていた（書き込まない側に倒す）
 * @graph-connects none
 */
export const ProductBacklogWriteSwitchSchema = z.enum(['on', 'off', 'unknown']);
/**
 * 読み取りの状態（BigQuery の値）。unavailable なら段階は判定できない。
 * @graph-connects none
 */
const SourceStatusSchema = z.enum(['ok', 'unavailable']);
/**
 * 案件一覧（開発タブ）。
 * - スプシを読めたとき: 区分（一覧の並び順）・形の問題・ずれ・Backlog の値の読み取り状態を返す
 * - スプシを読めなかったとき: 理由だけを返す（前に読んだ並びは返さない）
 *   - forbidden: 読む権限が無い（403。共有が外れた・認証の範囲が足りない）／ not_found: スプシかタブが無い（404・400）／
 *     unavailable: 一時的な障害など
 * readAt はスプシを読んだ時刻（ISO 8601）。writeWindows は反映しない時間帯の設定（画面の「HH:50 から」に使う）。
 * spreadsheetId は読み書きしているスプシ（本番か、設定で向けたコピー。画面の「スプシを開く」に使う）。
 * @graph-connects none
 */
export const ProductBacklogListResponseSchema = z.discriminatedUnion('sheetStatus', [
    z.object({
        sheetStatus: z.literal('ok'),
        readAt: z.string(),
        spreadsheetId: z.string(),
        writeSwitch: ProductBacklogWriteSwitchSchema,
        writeWindows: z.array(ProductBacklogWriteWindowSchema),
        sections: z.array(ProductBacklogSectionSchema),
        layoutIssues: z.array(ProductBacklogLayoutIssueSchema),
        drifts: z.array(ProductBacklogDriftSchema),
        backlogStatus: SourceStatusSchema,
        issueStatus: SourceStatusSchema,
    }),
    z.object({
        sheetStatus: z.literal('unreadable'),
        readAt: z.string(),
        writeSwitch: ProductBacklogWriteSwitchSchema,
        writeWindows: z.array(ProductBacklogWriteWindowSchema),
        reason: z.enum(['forbidden', 'not_found', 'unavailable']),
    }),
]);
//# sourceMappingURL=product-backlog.js.map